import { cn } from '@folio/lib';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip/tooltip';
import {
  CheckCircle2Icon,
  Loader2Icon,
  AlertCircleIcon,
  MinusCircleIcon,
  RotateCwIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { formatExtractionError } from './format-extraction-error';
import { Text } from '@folio/components/ui/text';

type ExtractionStatus = 'PENDING' | 'DONE' | 'FAILED' | 'SKIPPED';

interface ExtractionStatusBadgeProps {
  status: ExtractionStatus;
  errorMessage?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

const STATUS_CONFIG: Record<
  ExtractionStatus,
  {
    translationKey: string;
    bgClass: string;
    borderClass: string;
    textClass: string;
    iconClass: string;
  }
> = {
  PENDING: {
    translationKey: 'documents.extraction.status.pending',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    textClass: 'text-blue-700',
    iconClass: 'text-blue-600',
  },
  DONE: {
    translationKey: 'documents.extraction.status.done',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    textClass: 'text-green-700',
    iconClass: 'text-green-600',
  },
  FAILED: {
    translationKey: 'documents.extraction.status.failed',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    textClass: 'text-red-700',
    iconClass: 'text-red-600',
  },
  SKIPPED: {
    translationKey: 'documents.extraction.status.skipped',
    bgClass: 'bg-gray-50',
    borderClass: 'border-gray-200',
    textClass: 'text-gray-700',
    iconClass: 'text-gray-500',
  },
};

function StatusIcon({ status, iconClass }: { status: ExtractionStatus; iconClass: string }) {
  switch (status) {
    case 'PENDING':
      return <Loader2Icon className={cn('w-3 h-3 animate-spin', iconClass)} />;
    case 'DONE':
      return <CheckCircle2Icon className={cn('w-3 h-3', iconClass)} />;
    case 'FAILED':
      return <AlertCircleIcon className={cn('w-3 h-3', iconClass)} />;
    case 'SKIPPED':
      return <MinusCircleIcon className={cn('w-3 h-3', iconClass)} />;
  }
}

export function ExtractionStatusBadge({
  status,
  errorMessage,
  onRetry,
  isRetrying,
}: ExtractionStatusBadgeProps) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md border cursor-help',
            config.bgClass,
            config.borderClass,
          )}
        >
          <StatusIcon status={status} iconClass={config.iconClass} />
          <Text inline size="xs" weight="medium" className={config.textClass}>
            {t(config.translationKey)}
          </Text>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{t(config.translationKey)}</p>
        {status === 'FAILED' && errorMessage && (
          <Text size="xs" tone="muted" className="mt-1">
            {formatExtractionError(errorMessage, t)}
          </Text>
        )}
        {status === 'FAILED' && onRetry && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-7 text-xs"
            onClick={e => {
              e.stopPropagation();
              onRetry();
            }}
            disabled={isRetrying}
          >
            <RotateCwIcon className={cn('w-3 h-3 mr-1', isRetrying && 'animate-spin')} />
            {t('documents.extraction.retry')}
          </Button>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
